import { Component, ElementRef, inject, OnDestroy, OnInit, SecurityContext, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ProductTagHelperService } from 'projects/munhowen/src/app/services/product-tag-helper.service';
import { PriceFormat } from 'projects/shared/lib/components/shared/price/format/price-format.model';
import { ProductTag } from 'projects/shared/lib/components/shared/product/tag/product-tag.model';
import { ELoadingIndicatorStyle } from 'projects/shared/lib/enum/loading/loading-indicator-style.enum';
import { BasicError } from 'projects/shared/lib/error/basic-error';
import { ICartPreviewEnvironment } from 'projects/shared/lib/interfaces/environment/cart/cart-preview-environment.interface';
import { ICartCreationDialogService } from 'shared/services/ui/cart-creation-dialog/cart-creation-dialog-service.abstract';
import { ICartService, ICartServiceToken } from 'projects/shared/lib/services/data/cart/cart-service.interface';
import { IProductService, IProductServiceToken } from 'projects/shared/lib/services/data/product/product-service.interface';
import { IAuthService, IAuthServiceToken } from 'projects/shared/lib/services/infrastructure/authentication/auth-service.interface';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import {
  EError, EImageSize, EProductTag, EShoppingCartGetQueryType, EShoppingCartItemValidationError, EShoppingCartValidationError, PriceDto,
  ProductDetailDto, ProductIdentifierDto, ProductImageDto, ProductStockInformationDto, ProductStockInformationGetDto,
  ShoppingCartDto, ShoppingCartItemDto, ShoppingCartItemValidationErrorDto, ShoppingCartValidationResultDto
} from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { firstValueFrom, Observable, of, Subscription, switchMap } from 'rxjs';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { CartPreviewItem } from './item/cart-preview-item.model';
import { IUserService, IUserServiceToken } from 'projects/shared/lib/services/data/user/user-service.interface';
import { ErrorCodeService } from 'projects/shared/lib/services/infrastructure/error-code.service';
import { CartPreviewInfoConfig } from 'projects/shared/lib/components/shared/cart/preview/info/app-cart-preview-info.component';
import { IConfigurationService, IConfigurationServiceToken } from 'projects/shared/lib/services/infrastructure/configuration/configuration-service.interface';
import { IComparisonService, IComparisonServiceToken } from 'projects/shared/lib/services/infrastructure/comparison/comparison-service.interface';
import { IAssetsService, IAssetsServiceToken } from 'shared/services/ui/assets/assets-service.interface';

@Component({
  selector: 'app-cart-preview',
  templateUrl: './cart-preview.component.html',
  standalone: false
})
export class CartPreviewMHComponent implements OnInit, OnDestroy {
  private readonly configurationService: IConfigurationService = inject(IConfigurationServiceToken);

  @ViewChild('positionListContainer') positionListref: ElementRef<HTMLInputElement> | undefined;

  public isAuthenticated: boolean | undefined;

  public cart: ShoppingCartDto | undefined;
  public cartItemCount: number;
  public cartPreviewItemList: CartPreviewItem[];
  public cartPreviewItemInactiveList: ProductIdentifierDto[] | undefined;
  public deliveryDate: Date | undefined;

  public i18nShoppingCartPreviewCartInfo: { [k: string]: string } = {
    '=0': $localize`:@@ShoppingCartPreviewCartInfo0:No items in cart`,
    '=1': $localize`:@@ShoppingCartPreviewCartInfo1:1 item in cart`,
    'other': $localize`:@@ShoppingCartPreviewCartInfoOther:# items in cart`
  };

  private cartItemCountSubscription: Subscription | undefined;
  private cartSubscription: Subscription | undefined;
  protected authenticationSusbcription: Subscription | undefined;

  public shouldLoadPrices: boolean | undefined;
  public totalPrice: PriceDto | undefined;

  private cartPreviewSettings: ICartPreviewEnvironment | undefined;

  protected logger: LoggerService;
  protected readonly cartDataService: ICartService;
  protected readonly comparisonService: IComparisonService;
  protected readonly productService: IProductService;
  private readonly sanitizer: DomSanitizer;
  protected readonly authService: IAuthService;
  private router: Router;
  private assetsService: IAssetsService = inject(IAssetsServiceToken);
  private readonly userService: IUserService;

  public eLoadingIndicatorStyle: ELoadingIndicatorStyle;
  public isLoading: boolean | undefined;

  public cartValidationResult: ShoppingCartValidationResultDto | undefined;

  private cartValidationSubscription: Subscription | undefined;
  private permissionSubscription: Subscription | undefined;

  public shouldValidateCart: boolean | undefined;
  public shouldDisplayCartValidation: boolean | undefined;
  public shouldDisplayCartItemValidation: boolean | undefined;
  public shouldSplitValidAndInvalidCartItems: boolean | undefined;
  public isCheckoutAllowed: boolean | undefined;
  public cartIsValid: boolean | undefined;

  private customerNumberSubscription: Subscription | undefined;
  public customerNumber: string | undefined;
  public customerName: string | undefined;
  private maximumOrderAmount: number;
  public isCartDeliveryDateValid: boolean;

  public cartPreviewInfoConfig: CartPreviewInfoConfig = new CartPreviewInfoConfig();

  public constructor() {
    this.isLoading = false;
    this.shouldDisplayCartValidation = true;
    this.shouldValidateCart = ENVIRONMENT.cart.shouldValidateCarts;
    this.shouldDisplayCartItemValidation = true;
    this.shouldSplitValidAndInvalidCartItems = true;

    this.logger = ServiceLocator.injector.get(LoggerService);
    this.cartDataService = inject(ICartServiceToken);
    this.comparisonService = inject(IComparisonServiceToken);
    this.productService = inject(IProductServiceToken);
    this.sanitizer = ServiceLocator.injector.get(DomSanitizer);
    this.authService = inject(IAuthServiceToken);
    this.router = ServiceLocator.injector.get(Router);
    this.userService = inject(IUserServiceToken);

    this.cartPreviewSettings = ENVIRONMENT.cart.cartPreview;
    this.cartItemCount = 0;
    this.cartPreviewItemList = [];
    this.eLoadingIndicatorStyle = ELoadingIndicatorStyle.EMBEDDED;
    this.maximumOrderAmount = ENVIRONMENT.checkout.maximumOrderAmount;
    ServiceLocator.injector.get(IConfigurationServiceToken).checkoutConfigurationGet().subscribe((checkoutConfiguration) => {
      if (checkoutConfiguration.maximumOrderAmount != undefined) {
        this.maximumOrderAmount = checkoutConfiguration.maximumOrderAmount;
      }
    });
    this.isCartDeliveryDateValid = true;
  }

  public ngOnInit(): void {
    this.authenticationSusbcriptionInit();
    this.customerNumberSubscriptionInit();
    this.cartItemCountSubscriptionInit();
    this.cartSubscriptionInit();
    this.cartValidationSubscriptionInit();
    this.permissionSubscriptionInit();
    this.productStockInformationListLoadAsync(this.cartPreviewItemList);
  }

  public ngOnDestroy(): void {
    this.authenticationSusbcription?.unsubscribe();
    this.customerNumberSubscription?.unsubscribe();
    this.cartItemCountSubscription?.unsubscribe();
    this.cartSubscription?.unsubscribe();
    this.cartValidationSubscription?.unsubscribe();
    this.permissionSubscription?.unsubscribe();
  }

  private cartValidationSubscriptionInit() {
    if (this.shouldValidateCart && !this.cartValidationSubscription) {
      this.cartValidationSubscription = this.cartDataService.cartValidationChangedGet().subscribe(
        (cartValidationResult) => {
          if (this.shouldDisplayCartValidation) {
            this.cartValidationResult = cartValidationResult;
          }
          if (this.shouldDisplayCartItemValidation) {
            this.cartItemValidationUpdate(cartValidationResult?.itemValidationErrorList);
          }

          this.cartIsValid = this.cartValidationResult?.isValid;// != undefined && this.cartValidationResult.isValid == true;
          this.isCheckoutAllowed = this.isCheckoutAllowedStateGet();
          let deliveryDateValidationResult = cartValidationResult?.cartErrorList?.find(i => i.errorType == EShoppingCartValidationError.DeliveryDateIsInvalid);

          if (deliveryDateValidationResult != undefined) {
            this.isCartDeliveryDateValid = false;
          } else {
            this.isCartDeliveryDateValid = true;
          }
        }
      );
    }
  }

  private permissionSubscriptionInit() {
    if (!this.permissionSubscription) {
      this.permissionSubscription = this.authService.userPermissionChangedGet().subscribe(
        () => {
          this.shouldLoadPrices = this.shouldLoadPricesCurrentStateGet();
          this.isCheckoutAllowed = this.isCheckoutAllowedStateGet();
        }
      );
    }
  }

  authenticationSusbcriptionInit() {
    if (!this.authenticationSusbcription) {
      this.authenticationSusbcription = this.authService.authenticatedGet().subscribe(
        (isAuthenticated) => {
          this.isAuthenticated = isAuthenticated;
          this.shouldLoadPrices = this.shouldLoadPricesCurrentStateGet();
          this.isCheckoutAllowed = this.isCheckoutAllowedStateGet();
        }
      );
    }
  }

  cartItemCountSubscriptionInit() {
    if (!this.cartItemCountSubscription) {
      this.cartItemCountSubscription =
        this.cartDataService.cartCurrentItemCountChangedGet().subscribe(
          (cartItemCount: number) => {
            this.cartItemCount = cartItemCount;
          }
        );
    }
  }

  cartSubscriptionInit() {
    if (!this.cartSubscription) {
      this.cartSubscription =
        this.cartDataService.cartCurrentChangedGet().subscribe(
          (cart: ShoppingCartDto | undefined) => {

            if (cart == undefined) {
              return this.reset();
            }

            this.cartUpdate(cart);
          }
        );
    }
  }

  private customerNumberSubscriptionInit() {
    if (!this.customerNumberSubscription) {
      this.customerNumberSubscription = this.authService.customerNumberChangedGet().subscribe(
        (customerNumber: string | undefined) => {
          this.customerNumber = customerNumber;
          this.customerLoadAsync();
        }
      );
    }
  }

  private async customerLoadAsync() {
    if (this.isAuthenticated) {
      let customer = (await firstValueFrom(this.userService.customerGet(this.customerNumber))).customer;
      this.customerName = [customer?.customerName, customer?.customerName2, customer?.customerName3]
        .filter(n => n != undefined && n.length > 0)
        .join(" ");
    }
  }

  public async cartValidityUpdateClickAsync() {
    this.isLoading = true;
    if (this.cart?.cartNumber == undefined) {
      this.isLoading = false;
      return;
    }
    await firstValueFrom(this.cartDataService.cartValidate(this.cart.cartNumber)).then((result) => {
      this.cartDataService.cartValidationStateSet(this.cart, result.shoppingCartValidationResult);

      if (result.shoppingCartValidationResult?.isValid == true) {
        this.router.navigate(['/checkout', 'overview']);
      }

    }).catch((error) => {
      this.logger.error(error, this.constructor.name);
      let errorMessage = $localize`:@@CartPreviewCartValidateErrorMessage:Cart validation failed`;
      throw new BasicError(errorMessage, JSON.parse(error.response).errorCode);
    }).finally(() => {
      this.isLoading = false;
    });
  }

  private isCheckoutAllowedStateGet(): boolean | undefined {
    let hasOrderPermission = this.authService.canPlaceOrders();
    let isAuthenticated = this.authService.isAuthenticated();
    return isAuthenticated && hasOrderPermission && this.cartIsValid;
  }

  protected shouldLoadPricesCurrentStateGet(): boolean {
    return ENVIRONMENT.price.allowAnonymousPrice || (this.authService.isAuthenticated() && this.authService?.canSeePrices());
  }

  protected cartUpdate(cart: ShoppingCartDto) {
    let deliveryAddressHasChanged = this.cart?.deliveryAddressData?.addressIdentifier?.addressNumber != cart.deliveryAddressData?.addressIdentifier?.addressNumber;
    this.cart = cart;
    this.cartPreviewInfoConfig.cart = cart;

    this.cartPreviewItemInactiveList = cart.inactiveProductList;
    let cartDeliveryDate = undefined;

    if (this.cart?.deliveryDate != undefined) {
      cartDeliveryDate = new Date(this.cart?.deliveryDate);
      if (cartDeliveryDate.getTimezoneOffset() < 0) {
        cartDeliveryDate.setTime(cartDeliveryDate.getTime() - cartDeliveryDate.getTimezoneOffset() * 60 * 1000);
      } else {
        cartDeliveryDate.setTime(cartDeliveryDate.getTime() + cartDeliveryDate.getTimezoneOffset() * 60 * 1000);
      }
      cartDeliveryDate?.setUTCHours(12, 0, 0, 0);
    } else {
      cartDeliveryDate = undefined;
    }

    let deliveryDateHasChanged = !this.comparisonService.equalsDateByValues(this.deliveryDate, cartDeliveryDate);
    this.deliveryDate = cartDeliveryDate ? new Date(cartDeliveryDate) : undefined;

    if (cart?.totalPrice != undefined) {
      this.totalPrice = cart.totalPrice;
    }

    let localList: CartPreviewItem[] = [];
    Object.assign(localList, this.cartPreviewItemList);

    // lookup for known (already displayed items) - remove no longer valid items or update existing ones
    localList.forEach(
      (cartPreviewItem: CartPreviewItem) => {
        let match: ShoppingCartItemDto | undefined = cart?.itemList?.find(
          cartItem => this.comparisonService.equalsProductIdentifierByNumberOrGuid(cartItem.productIdentifier, cartPreviewItem.productIdentifier)
        );

        if (!match) {
          // remove
          let index = this.cartPreviewItemList.findIndex(item => item == cartPreviewItem);
          this.cartPreviewItemList.splice(index, 1);
        } else {
          // set reference and update
          cartPreviewItem.shoppingCartItem = match;
          if (match.totalPrice != undefined) {
            cartPreviewItem.shoppingCartItem = match;
            cartPreviewItem.priceFormat = new PriceFormat(match.totalPrice, match.orderUnit);
          }
        }
      }
    );

    // mapping for new (desired displayed items) - add new missing items
    let listToLoad: CartPreviewItem[] = [];
    cart?.itemList?.forEach(
      (cartItem: ShoppingCartItemDto) => {
        let match: CartPreviewItem | undefined = localList.find(
          (cartPreviewItem) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(cartPreviewItem.productIdentifier, cartItem.productIdentifier)
        );

        if (!match) {
          // not yet displayed -> add
          let cartPreviewitem = this.cartPreviewItemGetByCartItem(cartItem);
          //listToLoad.push(cartPreviewitem);
          listToLoad.unshift(cartPreviewitem);
        }
      }
    );

    this.cartPreviewItemListNewlyAddedInit(listToLoad);
    this.updateAllItemsByDateChange(deliveryDateHasChanged, deliveryAddressHasChanged);
  }

  private updateAllItemsByDateChange(deliveryDateHasChanged: boolean, deliveryAddressHasChanged: boolean) {
    if (this.cartPreviewItemList != undefined && this.cartPreviewItemList.length > 0) {
      if (deliveryDateHasChanged == true || deliveryAddressHasChanged == true) {
        this.productDetailListLoadAsync(this.cartPreviewItemList);
        this.productStockInformationListLoadAsync(this.cartPreviewItemList);
        this.updatePreviewItemPricesByCart(this.cart);
      }
    }
  }

  private updatePreviewItemPricesByCart(cart: ShoppingCartDto | undefined) {
    this.cartPreviewItemList.forEach(
      (cartPreviewItem) => {
        let match: ShoppingCartItemDto | undefined = cart?.itemList?.find(
          cartItem => this.comparisonService.equalsProductIdentifierByNumberOrGuid(cartItem.productIdentifier, cartPreviewItem.productIdentifier)
        );
        if (match == undefined || match.totalPrice == undefined) {
          return;
        }
        cartPreviewItem.priceFormat = new PriceFormat(match.totalPrice, match.orderUnit);
      }
    );
  }

  protected cartPreviewItemGetByCartItem(cartItem: ShoppingCartItemDto) {
    let priceFormat: PriceFormat | undefined;
    if (cartItem.totalPrice != undefined) {
      priceFormat = new PriceFormat(cartItem.totalPrice, cartItem.orderUnit);
    }
    let cartPreviewitem: CartPreviewItem = new CartPreviewItem(
      this.configurationService,
      {
        productIdentifier: cartItem.productIdentifier,
        shoppingCartItem: cartItem,
        priceFormat: priceFormat,
        minimumOrderAmount: 1,
        maximumOrderAmount: this.maximumOrderAmount,
        orderUnit: cartItem.orderUnit,
      }
    );
    return cartPreviewitem;
  }

  protected cartPreviewItemListNewlyAddedInit(listToLoad: CartPreviewItem[]) {
    if (listToLoad == undefined || listToLoad.length <= 0) {
      return;
    }

    this.cartPreviewItemList.unshift(...listToLoad);
    this.validationResultMap(listToLoad);
    this.productDetailListLoadAsync(listToLoad);
    this.productThumbnailListLoadAsync(listToLoad);
    this.productStockInformationListLoadAsync(listToLoad);

    this.scrollCartPreviewListToTop();
  }

  private reset() {
    this.cart = undefined;
    this.cartPreviewInfoConfig.cart = undefined;
    this.cartItemCount = 0;
    this.cartPreviewItemList = [];
    this.cartPreviewItemInactiveList = undefined;
    this.totalPrice = undefined;
    this.isLoading = false;
    return;
  }

  private async validationResultMap(cartPreviewItemList: CartPreviewItem[]) {
    cartPreviewItemList.forEach(
      (shoppingCartPreviewEntry: CartPreviewItem) => {
        let validationResult = this.cartValidationResult?.itemValidationErrorList?.find(
          (cartItemValidationResult) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(cartItemValidationResult.productIdentifier, shoppingCartPreviewEntry.productIdentifier)
        );
        shoppingCartPreviewEntry.validationResult = validationResult;
      }
    );
  }

  private async productDetailListLoadAsync(cartPreviewItemList: CartPreviewItem[]) {
    if (!cartPreviewItemList || cartPreviewItemList.length <= 0) {
      return;
    }
    let productIdentifierList: ProductIdentifierDto[] = [];

    cartPreviewItemList.forEach(
      (shoppingCartPreviewEntry: CartPreviewItem) => {
        let productIdentifier: ProductIdentifierDto | undefined = shoppingCartPreviewEntry.productIdentifier;
        if (!productIdentifier) {
          throw new Error('Missing productIdentifier upon loading data in comparison preview');
        }
        productIdentifierList.push(productIdentifier);
      }
    );

    try {
      let result = await firstValueFrom(this.productService.productDetailGet(false, true, true, false, true, false, true, this.deliveryDate, productIdentifierList));

      let productDetailDtoList: ProductDetailDto[] | undefined = result.productList;
      let productPromotionIdentifierList: ProductIdentifierDto[] = [];

      if (productDetailDtoList != undefined && productDetailDtoList.length > 0) {
        cartPreviewItemList.forEach(
          (shoppingCartPreviewItem: CartPreviewItem) => {

            shoppingCartPreviewItem.promotionDetail = undefined;

            let productDetailDto: ProductDetailDto | undefined = productDetailDtoList?.find(
              (productDetail: ProductDetailDto) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(
                productDetail.productIdentifier,
                shoppingCartPreviewItem.productIdentifier)
            );

            if (productDetailDto != undefined
              && productDetailDto.productIdentifier != undefined
              && productDetailDto?.hasPromotionDetail != undefined
              && productDetailDto?.hasPromotionDetail != null) {
              let isPromotionProduct = (productDetailDto.productTagList?.find(p => p == EProductTag.Promotion) != undefined && productDetailDto?.hasPromotionDetail);
              if (isPromotionProduct) {
                productPromotionIdentifierList?.push(productDetailDto.productIdentifier);
              }
            }

            shoppingCartPreviewItem.description = productDetailDto?.description;
            shoppingCartPreviewItem.minimumOrderAmount = productDetailDto?.minimumOrderAmount ? productDetailDto?.minimumOrderAmount : 1;
            shoppingCartPreviewItem.maximumOrderAmount = this.maximumOrderAmount;
            if (shoppingCartPreviewItem.orderUnit == undefined) {
              let salesUnitDefault = productDetailDto?.salesUnitList?.find(o => o.isPromotion == true);
              shoppingCartPreviewItem.orderUnit = salesUnitDefault;
            }

            let eProductTagList: EProductTag[] = [];
            Object.assign(eProductTagList, productDetailDto?.productTagList);

            if (shoppingCartPreviewItem.productStockInformation?.productReplacementExists) {

              if (eProductTagList == undefined) {
                eProductTagList = [];
              }

              eProductTagList.push(EProductTag.ProductReplacement);
            }

            shoppingCartPreviewItem.productTag = this.productTagListGet(eProductTagList);
          }
        );
      }

      if (productPromotionIdentifierList != undefined && productPromotionIdentifierList.length > 0) {
        let promotionDetailResult = await firstValueFrom(this.productService.productPromotionDetailGet(productPromotionIdentifierList));

        if (promotionDetailResult.promotionDetailList != undefined && promotionDetailResult.promotionDetailList.length > 0) {
          promotionDetailResult.promotionDetailList.forEach(promitionDetailItem => {
            cartPreviewItemList.forEach(cartPreviewItem => {
              if (this.comparisonService.equalsProductIdentifierByNumberOrGuid(cartPreviewItem.productIdentifier, promitionDetailItem.productIdentifier)) {
                cartPreviewItem.promotionDetail = promitionDetailItem;
              }
            });
          });
        }
      }
    } catch (error) {
      this.logger.warn(error, this.constructor.name);
    }

  }

  private async productThumbnailListLoadAsync(cartPreviewItemList: CartPreviewItem[]) {
    let productIdentifierList: ProductIdentifierDto[] = cartPreviewItemList.map(
      (shoppingCartPreviewEntry: CartPreviewItem) => {
        let productIdentifier: ProductIdentifierDto | undefined = shoppingCartPreviewEntry.productIdentifier;
        if (!productIdentifier) {
          throw new BasicError('Missing productIdentifier upon loading thumbnail in cart preview', ErrorCodeService.formatErrorCode(EError.F170080));
        }
        return productIdentifier;
      }
    );

    let thumbnailSize = this.cartPreviewSettings?.productThumbnailSize;

    if (thumbnailSize == undefined) {
      thumbnailSize = EImageSize.Medium;
    }

    try {
      this.productThumbnailListLoadFallbackAsync(cartPreviewItemList);

      let result = await firstValueFrom(this.productService.productThumbnailGet(thumbnailSize, productIdentifierList));

      let productImageDtoList: ProductImageDto[] | undefined = result.productList;
      cartPreviewItemList.forEach(
        (shoppingCartPreviewItem: CartPreviewItem) => {
          if (!productImageDtoList || productImageDtoList.length < 1) {
            return;
          }

          let thumbnailDto: ProductImageDto | undefined = productImageDtoList.find(
            (productImage: ProductImageDto) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(productImage.productIdentifier, shoppingCartPreviewItem.productIdentifier)
          );

          if (!thumbnailDto) {
            this.logger.info(`Thumbnail for product#: '${shoppingCartPreviewItem.productIdentifier?.productNumber}' could not be loaded.`, this.constructor.name);
            return;
          }

          let sanitizedThumbnail = this.sanitizer.sanitize(SecurityContext.URL, `data:${thumbnailDto.contentType};base64,${thumbnailDto.content}`);
          if (!sanitizedThumbnail) {
            return;
          }

          shoppingCartPreviewItem.thumbnail = sanitizedThumbnail;
        }
      );
    } catch (error) {
      this.logger.warn(error, this.constructor.name);
    }

  }

  private productThumbnailListLoadFallbackAsync(cartPreviewItemList: CartPreviewItem[]) {
    this.assetsService.productDefaultImageGetAsync().then(
      (productDefaultImageAsset) => {
        cartPreviewItemList?.forEach(
          (cartPreviewItem) => {

            if (cartPreviewItem.thumbnail != undefined) {
              // Fallback or desired / correct thumbnail is already set
              return;
            }

            let productDefaultImage = new ProductImageDto({
              productIdentifier: cartPreviewItem.productIdentifier,
              contentType: 'image/jpg',
              content: productDefaultImageAsset
            });

            let sanitizedThumbnail = this.sanitizer.sanitize(SecurityContext.URL, `data:${productDefaultImage.contentType};base64,${productDefaultImage.content}`);
            if (!sanitizedThumbnail) {
              return;
            }

            cartPreviewItem.thumbnail = sanitizedThumbnail;
          }
        );
      }
    );
  }

  private async productStockInformationListLoadAsync(cartPreviewItemList: CartPreviewItem[]) {
    let productStockInformationGetList: ProductStockInformationGetDto[] = cartPreviewItemList.map(
      (shoppingCartPreviewEntry: CartPreviewItem) => new ProductStockInformationGetDto({
        productIdentifier: shoppingCartPreviewEntry.productIdentifier,
        quantityUnit: shoppingCartPreviewEntry.orderUnit?.quantityUnit
      })
    );

    try {
      let result = await firstValueFrom(this.productService.productStockInformationGet(productStockInformationGetList, this.cart?.deliveryDate));

      let productStockInformationList: ProductStockInformationDto[] | undefined = result.productStockInformationList;
      if (productStockInformationList != undefined && productStockInformationList.length > 0) {
        cartPreviewItemList.forEach(
          (shoppingCartPreviewItem: CartPreviewItem) =>
            shoppingCartPreviewItem.productStockInformation = productStockInformationList?.find(
              (productStockInformation: ProductStockInformationDto) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(productStockInformation.productIdentifier, shoppingCartPreviewItem.productIdentifier)
            )
        );
      }

    } catch (error) {
      this.logger.warn(error, this.constructor.name);
    }

  }

  public async cartCreationDialogShowAsync(cart?: ShoppingCartDto | undefined, isCartCreation: boolean | undefined = true) {
    if (cart == undefined) {
      // try looking for an "old" one
      await firstValueFrom(this.cartDataService.cartGet(EShoppingCartGetQueryType.GetLatestOrCreateNew, false)).then((result) => {
        cart = result.shoppingCart;
      }).catch((error) => {
        this.logger.error(error, this.constructor.name);
        let errorMessage = $localize`:@@CartPreviewCreateCartErrorMessage:Could not create cart`;
        throw new BasicError(errorMessage, JSON.parse(error.response).errorCode);
      });
    }
    ServiceLocator.injector.get(ICartCreationDialogService).cartCreationDialogShow(
      () => { },
      cart,
      isCartCreation
    );
  }

  private cartItemValidationUpdate(cartItemValidationResultList: ShoppingCartItemValidationErrorDto[] | undefined) {
    let cartPreviewItemList: CartPreviewItem[] = [];
    Object.assign(cartPreviewItemList, this.cartPreviewItemList);
    this.cartPreviewItemList = [];

    cartPreviewItemList.forEach(
      (cartItem) => {
        cartItem.validationResult = undefined;
      }
    );

    cartItemValidationResultList?.forEach(
      (cartItemValidationResult) => {
        let cartItem = cartPreviewItemList.find(i =>
          this.comparisonService.equalsProductIdentifierByNumberOrGuid(cartItemValidationResult.productIdentifier, i.productIdentifier)
        );

        if (cartItem == undefined) {
          return;
        }

        cartItem.validationResult = cartItemValidationResult;
      });

    this.cartPreviewItemList = cartPreviewItemList;

    this.cartItemValidationAdditionalInformationLoadAsync(cartPreviewItemList);
  }

  private async cartItemValidationAdditionalInformationLoadAsync(cartPreviewItemList: CartPreviewItem[]) {
    let outOfStockCartPreviewItemList = cartPreviewItemList.filter(
      (cartPreviewItem) => cartPreviewItem.validationResult?.shoppingCartItemValidationError === EShoppingCartItemValidationError.ProductIsOutOfStock);

    try {
      let productStockInformationList = await firstValueFrom(this.stockInformationLoad(outOfStockCartPreviewItemList));
      productStockInformationList?.forEach(
        (productStockInformation) => {
          let cartPreviewItem = cartPreviewItemList.find(
            (cartPreviewItem) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(
              productStockInformation.productIdentifier,
              cartPreviewItem.productIdentifier
            )
          );

          if (cartPreviewItem == undefined) {
            return;
          }

          cartPreviewItem.availableQuantity = productStockInformation.availableQuantity;
        }
      );
    } catch (error) {
      this.logger.warn(error, this.constructor.name);
    }
  }

  private stockInformationLoad(cartItemList: CartPreviewItem[]): Observable<ProductStockInformationDto[] | undefined> {
    if (cartItemList == undefined || cartItemList.length <= 0) {
      return of(undefined);
    }

    let productStockInformationGetList: ProductStockInformationGetDto[] = [];
    cartItemList.forEach(
      (cartItem) => {
        productStockInformationGetList.push(new ProductStockInformationGetDto({
          productIdentifier: cartItem.productIdentifier,
          quantityUnit: cartItem.orderUnit?.quantityUnit,
        }));
      }
    );

    return this.productService.productStockInformationGet(productStockInformationGetList, this.cart?.deliveryDate).pipe(
      switchMap(
        (result) => of(result.productStockInformationList)
      )
    );
  }

  private productTagListGet(eProductTag: EProductTag[] | undefined) {
    if (eProductTag == undefined) {
      return;
    }

    let eProductTagList: EProductTag[] = [];
    Object.assign(eProductTagList, eProductTag);

    if (eProductTagList == undefined) {
      eProductTagList = [];
    }

    let productTagList: ProductTag[] = [];

    eProductTagList?.forEach(
      (eProductTag) => {
        let productTag = ProductTagHelperService.productTagGetByEProductTag(eProductTag);
        productTagList.push(productTag);
      }
    );

    return productTagList;
  }

  private scrollCartPreviewListToTop(): void {
    this.positionListref?.nativeElement.scrollTo(0, 0);
  }

}
